const MARKETING_HOME_PATH = `/retailers/${ENV.SLUG}/marketing`;
const MARKETING_CAMPAIGNS_PATH = `/retailers/${ENV.SLUG}/marketing/campaigns`;
const MARKETING_CAMPAIGNS_CALENDAR_PATH = `/retailers/${ENV.SLUG}/marketing/campaigns/calendar`;
const MARKETING_CONTENT_PREFERENCES_PATH = `/retailers/${ENV.SLUG}/marketing/campaigns/content-preferences`;
const MARKETING_BRAND_KIT_PATH = `/retailers/${ENV.SLUG}/marketing/campaigns/brand-kit`;
const MARKETING_INTEGRATIONS_PATH = `/retailers/${ENV.SLUG}/marketing/integrations`;
const MARKETING_INTEGRATIONS_INSTAGRAM_PATH = `/retailers/${ENV.SLUG}/marketing/integrations/instagram`;
const MARKETING_INTEGRATIONS_INSTAGRAM_CONNECT_PATH = `/retailers/${ENV.SLUG}/marketing/integrations/instagram/connect`;
const MARKETING_INSTAGRAM_STATUS_PATH = `/retailers/${ENV.SLUG}/integrations/marketing_instagram/status`;
const MARKETING_SOCIAL_ACCOUNTS_PATH = `/retailers/${ENV.SLUG}/marketing/social_accounts`;
const MARKETING_SOCIAL_ACCOUNTS_CONNECT_PATH = `/retailers/${ENV.SLUG}/marketing/social_accounts/connect_url`;
// Headless OAuth selection step: our own "pick your page" screen instead of Zernio's.
const MARKETING_SOCIAL_ACCOUNTS_SELECT_PATH = `/retailers/${ENV.SLUG}/marketing/social_accounts/select`;
const MARKETING_SOCIAL_ACCOUNTS_CONNECT_OPTIONS_PATH = `/retailers/${ENV.SLUG}/marketing/social_accounts/connect_options`;
const MARKETING_SOCIAL_ACCOUNTS_CONNECT_SELECT_PATH = `/retailers/${ENV.SLUG}/marketing/social_accounts/connect_select`;
const MARKETING_INSTAGRAM_OAUTH_PATH = '/retailer_users/auth/facebook/marketing_instagram';
const MARKETING_INSTAGRAM_DISCONNECT_PATH = `/retailers/${ENV.SLUG}/integrations/disconnect_marketing_instagram`;
const MARKETING_INSTAGRAM_PICK_TOKEN_PATH = '/retailers/pick_marketing_ig_token';
const MARKETING_BRAND_KIT_INSTAGRAM_CONNECT_PATH = MARKETING_INTEGRATIONS_INSTAGRAM_CONNECT_PATH;
const MARKETING_REACH_META_ADS_PATH = `/retailers/${ENV.SLUG}/marketing/reach/meta-ads`;
const MARKETING_REACH_PATH = MARKETING_REACH_META_ADS_PATH;
const MARKETING_POSTS_PATH = `/retailers/${ENV.SLUG}/marketing/posts`;

const isMarketingCampaignsPath = (pathname) => (
  pathname === MARKETING_CAMPAIGNS_PATH
  || pathname.startsWith(`${MARKETING_CAMPAIGNS_PATH}/`)
  || pathname.startsWith(`${MARKETING_POSTS_PATH}/`)
);

const isMarketingIntegrationsPath = (pathname) => (
  pathname === MARKETING_INTEGRATIONS_PATH
  || pathname.startsWith(`${MARKETING_INTEGRATIONS_PATH}/`)
  || pathname === MARKETING_SOCIAL_ACCOUNTS_SELECT_PATH
);

const isMarketingReachPath = (pathname) => (
  pathname === MARKETING_REACH_META_ADS_PATH
  || pathname.startsWith(`/retailers/${ENV.SLUG}/marketing/reach/`)
);

// Exact paths only. The Instagram detail path is a prefix of the connect path,
// so a startsWith check would open or close the wrong screen.
const isMarketingOnboardingEscapePath = (pathname, search = '') => {
  if (pathname === MARKETING_INTEGRATIONS_INSTAGRAM_CONNECT_PATH) return true;
  if (pathname === MARKETING_INTEGRATIONS_INSTAGRAM_PATH) return true;
  if (pathname !== MARKETING_INTEGRATIONS_PATH) return false;

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.has('handle') || params.has('connect_error');
};

const getMarketingCampaignPath = (id) => `${MARKETING_CAMPAIGNS_PATH}/${id}`;

const MARKETING_POST_FROM_CALENDAR = 'calendar';
const MARKETING_CALENDAR_VIEWS = ['week', 'month', 'list'];
const MARKETING_CALENDAR_DEFAULT_VIEW = 'week';

const NESTED_POST_PATH_PATTERN = new RegExp(
  `^${MARKETING_CAMPAIGNS_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^/]+)/posts/([^/]+)(?:/design)?$`
);

const STANDALONE_POST_PATH_PATTERN = new RegExp(
  `^${MARKETING_POSTS_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^/]+)$`
);

const appendSearchParams = (path, params = {}) => {
  const entries = Object.entries(params).filter(([, value]) => value);
  if (!entries.length) return path;

  return `${path}?${new URLSearchParams(entries).toString()}`;
};

const getSearchParam = (search = '', key) => (
  new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(key) || ''
);

const getMarketingPostFromParam = (search = '') => getSearchParam(search, 'from');

const normalizeMarketingCalendarView = (value) => (
  MARKETING_CALENDAR_VIEWS.includes(value) ? value : MARKETING_CALENDAR_DEFAULT_VIEW
);

const getMarketingCalendarViewParam = (search = '') => (
  normalizeMarketingCalendarView(getSearchParam(search, 'view'))
);

const CALENDAR_DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getMarketingCalendarDateParam = (search = '') => {
  const value = getSearchParam(search, 'date');
  return CALENDAR_DATE_PARAM_PATTERN.test(value) ? value : '';
};

const getMarketingCalendarCampaignParam = (search = '') => getSearchParam(search, 'campaign');

const getMarketingCalendarPlace = ({ view, date, campaign } = {}) => ({
  view: normalizeMarketingCalendarView(view),
  date: CALENDAR_DATE_PARAM_PATTERN.test(date) ? date : '',
  campaign: campaign || ''
});

const getMarketingCalendarPath = ({ view, date, campaign } = {}) => (
  appendSearchParams(
    MARKETING_CAMPAIGNS_CALENDAR_PATH,
    getMarketingCalendarPlace({ view, date, campaign })
  )
);

const getMarketingPostNavParams = (search = '') => {
  const from = getMarketingPostFromParam(search);
  if (!from) return {};
  if (from !== MARKETING_POST_FROM_CALENDAR) return { from };

  return {
    from,
    ...getMarketingCalendarPlace({
      view: getSearchParam(search, 'view'),
      date: getSearchParam(search, 'date'),
      campaign: getSearchParam(search, 'campaign')
    })
  };
};

const getCalendarAwareSearchParams = ({
  from, view, date, campaign
} = {}) => {
  if (from !== MARKETING_POST_FROM_CALENDAR) return { from };

  return {
    from,
    ...getMarketingCalendarPlace({ view, date, campaign })
  };
};

const getMarketingPostPath = (campaignId, postId, {
  from, view, date, campaign
} = {}) => {
  const path = !campaignId && postId
    ? `${MARKETING_POSTS_PATH}/${postId}`
    : postId === undefined
      ? `${MARKETING_POSTS_PATH}/${campaignId}`
      : `${MARKETING_CAMPAIGNS_PATH}/${campaignId}/posts/${postId}`;

  return appendSearchParams(path, getCalendarAwareSearchParams({
    from, view, date, campaign
  }));
};

const getMarketingPostDesignPath = (campaignId, postId, {
  from, view, date, campaign
} = {}) => (
  appendSearchParams(
    `${MARKETING_CAMPAIGNS_PATH}/${campaignId}/posts/${postId}/design`,
    getCalendarAwareSearchParams({
      from, view, date, campaign
    })
  )
);

const getMarketingPostBackPath = (campaignId, search = '') => (
  getMarketingPostFromParam(search) === MARKETING_POST_FROM_CALENDAR || !campaignId
    ? getMarketingCalendarPath(getMarketingPostNavParams(search))
    : getMarketingCampaignPath(campaignId)
);

const parseNestedMarketingPostPath = (pathname) => {
  const match = pathname.match(NESTED_POST_PATH_PATTERN);
  if (!match) return null;

  return {
    campaignId: match[1],
    postId: match[2]
  };
};

const parseStandaloneMarketingPostPath = (pathname) => {
  const match = pathname.match(STANDALONE_POST_PATH_PATTERN);
  if (!match) return null;

  return {
    campaignId: '',
    postId: match[1]
  };
};

const isMarketingPostDesignPath = (pathname) => (
  parseNestedMarketingPostPath(pathname) !== null && pathname.endsWith('/design')
);

const isMarketingPostEditPath = (pathname) => {
  if (isMarketingPostDesignPath(pathname)) return false;
  return parseNestedMarketingPostPath(pathname) !== null
    || parseStandaloneMarketingPostPath(pathname) !== null;
};

const extractPostRouteParams = (pathname) => (
  parseNestedMarketingPostPath(pathname) || parseStandaloneMarketingPostPath(pathname)
);

const extractPostId = (pathname) => extractPostRouteParams(pathname)?.postId || '';

const extractPostCampaignId = (pathname) => extractPostRouteParams(pathname)?.campaignId || '';

const isMarketingCampaignPostPath = (pathname) => (
  isMarketingPostEditPath(pathname) || isMarketingPostDesignPath(pathname)
);

const isMarketingCampaignDetailPath = (pathname) => (
  pathname.startsWith(`${MARKETING_CAMPAIGNS_PATH}/`)
  && pathname !== `${MARKETING_CAMPAIGNS_PATH}/new`
  && pathname !== MARKETING_CAMPAIGNS_CALENDAR_PATH
  && pathname !== MARKETING_CONTENT_PREFERENCES_PATH
  && pathname !== MARKETING_BRAND_KIT_PATH
  && !isMarketingCampaignPostPath(pathname)
);

const extractCampaignId = (pathname) => (
  pathname.replace(`${MARKETING_CAMPAIGNS_PATH}/`, '').split('/')[0]
);

export const MARKETING_GLOBAL_ROUTES = [
  {
    icon: 'icon-dashboard-left fs-20',
    useCssIcon: true,
    routeProps: {
      key: 'dashboard',
      i18nTitleKey: 'marketing_routes.dashboard',
      path: MARKETING_HOME_PATH,
      urlLinkPath: MARKETING_HOME_PATH
    }
  },
  {
    icon: 'icon-campaigns-left fs-20',
    useCssIcon: true,
    routeProps: {
      key: 'campaigns',
      i18nTitleKey: 'marketing_routes.campaigns',
      path: MARKETING_CAMPAIGNS_PATH,
      urlLinkPath: MARKETING_CAMPAIGNS_CALENDAR_PATH
    }
  },
  {
    icon: 'icon-integrations-left fs-20',
    useCssIcon: true,
    routeProps: {
      key: 'integrations',
      i18nTitleKey: 'marketing_routes.integrations',
      path: MARKETING_INTEGRATIONS_PATH,
      urlLinkPath: MARKETING_INTEGRATIONS_PATH
    }
  }
];

export const MARKETING_CAMPAIGNS_SUB_ROUTES = [
  {
    icon: 'calendar-outline',
    routeProps: {
      key: 'calendar',
      i18nTitleKey: 'marketing_routes.calendar',
      path: MARKETING_CAMPAIGNS_CALENDAR_PATH,
      urlLinkPath: MARKETING_CAMPAIGNS_CALENDAR_PATH
    }
  },
  {
    icon: 'campaign',
    routeProps: {
      key: 'campaigns',
      i18nTitleKey: 'marketing_routes.campaigns_list',
      path: MARKETING_CAMPAIGNS_PATH,
      urlLinkPath: MARKETING_CAMPAIGNS_PATH
    }
  },
  {
    icon: 'settings-outline',
    routeProps: {
      key: 'content_preferences',
      i18nTitleKey: 'marketing_routes.content_preferences',
      path: MARKETING_CONTENT_PREFERENCES_PATH,
      urlLinkPath: MARKETING_CONTENT_PREFERENCES_PATH
    }
  },
  {
    icon: 'pricetags-outline',
    routeProps: {
      key: 'brand_kit',
      i18nTitleKey: 'marketing_routes.brand_kit',
      path: MARKETING_BRAND_KIT_PATH,
      urlLinkPath: MARKETING_BRAND_KIT_PATH
    }
  }
];

export const MARKETING_INTEGRATIONS_SUB_ROUTES = [
  {
    icon: 'instagram-fill',
    routeProps: {
      key: 'social',
      i18nTitleKey: 'marketing_routes.integrations_social',
      path: MARKETING_INTEGRATIONS_PATH,
      urlLinkPath: MARKETING_INTEGRATIONS_PATH
    }
  }
];

export const MARKETING_REACH_SUB_ROUTES = [
  {
    icon: 'facebook-fill',
    routeProps: {
      key: 'meta_ads',
      i18nTitleKey: 'marketing_routes.meta_ads',
      path: MARKETING_REACH_META_ADS_PATH,
      urlLinkPath: MARKETING_REACH_META_ADS_PATH
    }
  }
];

export {
  MARKETING_HOME_PATH,
  MARKETING_CAMPAIGNS_PATH,
  MARKETING_CAMPAIGNS_CALENDAR_PATH,
  MARKETING_CONTENT_PREFERENCES_PATH,
  MARKETING_BRAND_KIT_PATH,
  MARKETING_BRAND_KIT_INSTAGRAM_CONNECT_PATH,
  MARKETING_INTEGRATIONS_PATH,
  MARKETING_INTEGRATIONS_INSTAGRAM_PATH,
  MARKETING_INTEGRATIONS_INSTAGRAM_CONNECT_PATH,
  MARKETING_INSTAGRAM_STATUS_PATH,
  MARKETING_SOCIAL_ACCOUNTS_PATH,
  MARKETING_SOCIAL_ACCOUNTS_CONNECT_PATH,
  MARKETING_SOCIAL_ACCOUNTS_SELECT_PATH,
  MARKETING_SOCIAL_ACCOUNTS_CONNECT_OPTIONS_PATH,
  MARKETING_SOCIAL_ACCOUNTS_CONNECT_SELECT_PATH,
  MARKETING_INSTAGRAM_OAUTH_PATH,
  MARKETING_INSTAGRAM_DISCONNECT_PATH,
  MARKETING_INSTAGRAM_PICK_TOKEN_PATH,
  MARKETING_REACH_PATH,
  MARKETING_REACH_META_ADS_PATH,
  MARKETING_POSTS_PATH,
  isMarketingCampaignsPath,
  isMarketingIntegrationsPath,
  isMarketingOnboardingEscapePath,
  isMarketingReachPath,
  MARKETING_POST_FROM_CALENDAR,
  MARKETING_CALENDAR_VIEWS,
  MARKETING_CALENDAR_DEFAULT_VIEW,
  getMarketingCampaignPath,
  getMarketingCalendarPath,
  getMarketingCalendarViewParam,
  getMarketingCalendarDateParam,
  getMarketingCalendarCampaignParam,
  normalizeMarketingCalendarView,
  getMarketingPostPath,
  getMarketingPostDesignPath,
  getMarketingPostFromParam,
  getMarketingPostNavParams,
  getMarketingPostBackPath,
  isMarketingPostEditPath,
  isMarketingPostDesignPath,
  isMarketingCampaignPostPath,
  extractPostId,
  extractPostCampaignId,
  extractPostRouteParams,
  isMarketingCampaignDetailPath,
  extractCampaignId
};
